import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { DynamicModalLoaderService } from '@ispm/layout/services/dynamic-modal-loader.service';
import { HttpParams } from '@angular/common/http';
import {defer, from, Observable, of, Subject, Subscription} from 'rxjs';
import { isEqual, isEmpty,forEach,find,get,sortBy } from 'lodash';
import {map, take,takeUntil} from 'rxjs/operators';
import { INotesTitleConfig } from '../../interfaces/notes-title-config.interface';
import { Note } from '../../interfaces/note';
import { NotesModalWrapperComponent } from '../notes-modal-wrapper/notes-modal-wrapper.component';
import { PlaceholderModalComponent } from '@ispm/layout/components/modals/placeholder-modal/placeholder-modal.component';
import { SearchService } from '@ispm/shared/dynamic-form/services/search.service';
import { modalResponse } from '@ispm/layout/constants/modal-responses.enum';
import { AuthenticationService } from '@ispm/user/services/authentication.service';
import { NotesService } from '@ispm/user/services/notes.service';

@Component({
  selector: 'gen-aberration-comments',
  templateUrl: './aberration-comments.component.html',
  styleUrls: ['./aberration-comments.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default
})
export class AberrationCommentsComponent implements OnInit, OnChanges {

  @Input() aberration: any;
  @Input() searchTerm: string[];
  @Input() nonAberation: boolean = false;
  @Input() queryType: boolean;
  @Input() referenceTypes: string[];
  @Input() lastTooltipText: string = "";
  @Input() maxTooltipHeight?: number;
  @Input() maxTooltipWidth?: number;
  @Input() directData?: boolean = false;//Use the data array of notes provided
  @Input() revisionPath: string; // workflowrevision path from the table which is the context for notes fetching

  @Input() notes: [];
  @Input() activity: [];
  @Input() currentUserId: string = "";

  @Input() set titleConfig(config: INotesTitleConfig) {
    if (config && config.main) {
      this.title = this.aberration[config.main.title] ? this.aberration[config.main.title] : this.aberration[config.fallback.title];
      this.subTitle = this.aberration[config.main.subtitle] ? this.aberration[config.main.subtitle] : this.aberration[config.fallback.subtitle];
    }
    else {
      return;
    }
  }

  @Output() noteAddedEmit: EventEmitter<any> = new EventEmitter();
  @Output() noteCompletedEmit: EventEmitter<any> = new EventEmitter();

  public destroyer$: Subject<any> = new Subject();
  public tooltipText: Observable<string> = of("");
  private countObs$: Subscription;
  DEFAULT_RESPONSE = "No data available";

  private modalSubscriber$: Subscription;
  private title: string;
  private subTitle: string;
  public newNotesCount:number = 0;

  get contextId(): string {
    return this.nonAberation?this.searchTerm:((this.aberration && this.aberration.id) || this.searchTerm);
  }

  get contextType(): string | string [] {
    return this.referenceTypes || 'Aberration';
  }

  constructor(
    private dynamicModalLoader: DynamicModalLoaderService,
    private authService: AuthenticationService,
    private notesService: NotesService,
    private searchService: SearchService,
    private _changeDetector: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
   const {aberration, searchTerm, referenceTypes} = changes;
    if (
      (aberration && !aberration.firstChange && aberration.currentValue.id !== aberration.previousValue.id) || //change in contextId - aberration has changed
      (!(this.aberration && this.aberration.id) && (searchTerm && !searchTerm.firstChange && !isEqual(searchTerm.currentValue, searchTerm.previousValue))) ||  //change in contextId - searchTerm has changed
      (referenceTypes && !referenceTypes.firstChange && !isEqual(referenceTypes.currentValue, referenceTypes.previousValue)) //change in contextType
    ) {
      this.updateTooltipTextObservable();
    }
  }
   isAdminUser(){
    return this.authService.isAdminUser();
  }

  ngOnInit() {
    if (this.nonAberation) {
      this.getNewNotesCount();
    }
      this.updateTooltipTextObservable(true);
  }

  openPopup(event) {
    event.stopPropagation();
    if (this.modalSubscriber$) { return; }
    const subprop = this.subTitle ? `(${this.subTitle})` : '';
    this.modalSubscriber$ = this.dynamicModalLoader.popupModal(PlaceholderModalComponent,
      {
        title: `<i class="icon__notifications"></i> Notes - ${this.title || ''} ${subprop}`,
        classes: 'Aberration_Comments',
        iconName: 'icon__notifications',
        componentToLoad: NotesModalWrapperComponent,
        payload: {
          actorType: 'Practitioner',
          contextType: this.contextType,
          newNotes: false,
          actorId: this.authService.user.info.id,
          contextId: this.contextId,
          nonAberation:this.nonAberation,
          queryType:this.queryType,
          filters: false
        },
        approveCallback: (newNote) => this.AddNote(newNote),
      })
      .filter(response => response.payload)
      .map(response => response.payload)
      .subscribe(
        (response: any) => {
          switch (<modalResponse> response.status) {
            case modalResponse.approve:
              // this.updatedOrder.emit(response.data);
              break;
            case modalResponse.decline:
            default:
              return false;
          }
          this.modalSubscriber$.unsubscribe();
          this.modalSubscriber$ = null;
        },
        () => {
        },
        () => {
          if (this.nonAberation) {
            this.newNotesCount = 0;
            this._changeDetector.markForCheck();
          }
          this.modalSubscriber$.unsubscribe();
          this.modalSubscriber$ = null;
        }
      );
  }

  ngOnDestroy(): void {
    if (this.modalSubscriber$) {
      this.modalSubscriber$.unsubscribe();
    }
    if(this.countObs$) {
      this.countObs$.unsubscribe();
    }
    this.destroyer$.complete();
  }

  private AddNote(newNote: any) {
    if (!this.aberration.hasNotes) {
      this.aberration.hasNotesUpdate = true;
    }

    this.aberration.hasNotes = true;
    newNote.hasNotes = true;
    this.noteAddedEmit.emit(newNote);
    this.noteCompletedEmit.emit(true);
    this.updateTooltipTextObservable();
  }

  private extractTooltipText(notes: Note[] | null | undefined): string {
    if (Array.isArray(notes) && (notes.length > 0)) {
      this.aberration.hasNotes = true;
      return notes[0].tooltipText || notes[0].text || this.DEFAULT_RESPONSE;
    } else {
      this.aberration.hasNotes = false;
      return this.DEFAULT_RESPONSE;
    }
  }

  private updateTooltipTextObservable(initialRequest = false) {
    if (!this.nonAberation) {
      this.tooltipText = defer(() => {
        if (!initialRequest) {
          return from(this.notesService.getWithContextType(this.contextId, this.contextType, this.authService.user.info.id))
            .pipe(takeUntil(this.destroyer$.asObservable()),
              map((response: any) => response.body),//Post migration of the service its data to body
              map((notes: Note[]) => (notes && notes.length && (notes[0].tooltipText || notes[0].text)) || this.DEFAULT_RESPONSE),
            )
        } else {
          if (!(isEmpty(this.revisionPath))) {
            return this.notesService.getBulkNote(get(this.aberration, this.revisionPath)).pipe(
              take(1),takeUntil(this.destroyer$.asObservable()),
              map((data) => {
                const notes = sortBy(get(data,'notes'), ['meta.lastUpdated']);
                return this.extractTooltipText(notes);
              }));
          } else {
            const params = new HttpParams({
              fromObject: {
                context: this.contextId,
                contextType: this.contextType,
                actor: this.authService.user.info.id,
                latestFirst: `${true}`,
                exact: `${this.contextType === 'WorkflowRequest'}`
              }
            });
            return this.searchService.query(`/note?${params.toString()}`, "").pipe(
              take(1),
              map((notes: Note[]) => this.extractTooltipText(notes))
            )
          }
        }
      })
    }
  }
  /* TODO show the count of unread messages rather than text
  else {
      this.tooltipText = defer(() => from(this.notesService.get(this.contextId, this.contextType, this.authService.user.info.id)))
       .pipe(
         map((response: any) => response.data),
         map((notes: Note[]) => (notes.filter((note) => !note.read).length).toString())
       )

   }*/

  private getNewNotesCount() {
        const params = new HttpParams({fromObject: {
            context: this.contextId,
            actor: this.authService.user.info.id
          }});

        if(this.directData){
          this.newNotesCount = this.calculateUnread(this.notes,this.activity,this.currentUserId);
         // this._changeDetector.markForCheck();
        }
        else if(!(isEmpty(this.revisionPath))){
        this.notesService.getBulkNote(get(this.aberration,this.revisionPath)).pipe(take(1),takeUntil(this.destroyer$.asObservable()))
            .subscribe((data) => {
              const allActivity = get(data,'Activity');
              const notes = get(data,'notes');

              if(isEmpty(notes)){
                return;
              }
              this.newNotesCount = this.calculateUnread(notes,allActivity,this.currentUserId);
              //this.notes = get(data,'AllNote'); This will create problem when user is adding the notes opening and closing will not have the latest values
              //this.activity = get(data,'Activity');
              this._changeDetector.markForCheck();
            });
        }else {
    this.countObs$ = this.searchService.query(`/note?${params.toString()}`,"",{},false,{'[forceFetch]': true})
      .subscribe((notes: Note[]) => {
        if(!isEmpty(notes)) {
          this.newNotesCount = notes.filter((note) => !note.read).length;
        }
        else {
          this.newNotesCount = 0;
        }
        this._changeDetector.markForCheck();
      });
  }}

  private calculateUnread(notes,activities,currentUserId){
    let unreadCount = notes.length;
    forEach(notes, function (note) {
      let read = find(activities, function (activity) {
          return get(activity,'context.0.reference') == get(note,'id') && get(activity,'actor.reference') == currentUserId;
        });
      if(get(read,'action',undefined) === 'read'){
          unreadCount--;
      }
      // note.read = note.read || !!(_.get(read, 'action', undefined) === 'read');
     });
    return unreadCount;
      }
}
